import React, { useCallback, useEffect, useRef, useState } from "react";
import { COLORS } from "../../../constants/colors";
import { Box, Button, Stack, useTheme } from "@mui/material";
import { DashboardHeaderTools } from "./headerTools";
import PropTypes from "prop-types";
import { AddButton } from "../../common/addButton";
import { useLocation, useNavigate } from "react-router-dom";
import { postDashboard } from "../../../api/dashboard";
import { getSortedDashboards } from "../dashboardUtil";
import { DASHBOARD_MODE } from "../constants";
import { useSelector } from "react-redux";
import { selectSideBarState } from "../../../model/slice/sideBarSettingSlice";
import { alphabeticalSortCompareFunction } from "../../../util/util";

const useStyles = (theme) => ({
  tabContainer: {
    display: "flex",
    position: "fixed",
    width: "calc(100vw - 77px)",
    top: "50px",
    zIndex: "999",
    backgroundColor: COLORS.tabSelectorColor,
  },
  tabBase: {
    position: "relative",
    zIndex: 50,
    height: "48px",
    width: "calc(100dvw - 95px)",
    [theme.breakpoints.down("sm")]: {
      height: "40px",
      borderRadius: "0 5px 5px 0",
    },
    backgroundColor: COLORS.tabSelectorColor,
    cursor: "pointer",
    display: "flex",
    "& p": {
      color: "#ffffff",
      fontSize: "12px",
      fontWeight: "500",
      fontFamily: "Inter",
      [theme.breakpoints.down("sm")]: {
        fontSize: "8px",
      },
    },
    userSelect: "none",
    MozUserSelect: "none",
    WebkitUserSelect: "none",
  },
  tabFree: {
    "& p": {
      opacity: 0.7,
    },
  },
  tabSelected: {
    "& p": {
      opacity: 1.0,
      color: "#19b2ab",
    },
    borderBottom: "solid 6px #19b2ab",
  },
});

export const DashboardHeader = (props) => {
  const {
    currentVessel,
    mode,
    setMode,
    setIsDrawerOpen,
    dashboards,
    setDashboards,
    selectedDashboard,
    setSelectedDashboard,
    setAddComment,
  } = props;
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const location = useLocation();
  const [isSaving, setIsSaving] = useState(false);
  const expectedChangeRef = useRef(false);
  const latestDataRef = useRef(selectedDashboard);
  const modeRef = useRef(mode);
  const [updateFlag, setUpdateFlag] = useState(false);
  const sideBarStatus = useSelector(selectSideBarState);

  // these useEffect are for updating dashboard when browser back and change vessel, move another page by sidebar

  // when user browser back, Connect need to update dashboards but it cause displaying wrong dashboards if user change selected vessel
  // so we need this update flag useEffect (if user change the selected vessel, this component is re-mounted so this updateFlag reset to false)
  useEffect(() => {
    if (updateFlag) {
      const updateDashboards = async () => {
        const sortedDashboards = await getSortedDashboards(currentVessel.id);
        setDashboards(sortedDashboards);
        setUpdateFlag(false);
      };
      updateDashboards();
    }
  }, [updateFlag]);

  // need to call this function when user leave current page so this function access latest data by such useRef way
  const handleFunction = useCallback(async (pathname) => {
    // avoid duplicate saving when changing selected dashboard by header dashboard selector
    if (expectedChangeRef.current) {
      expectedChangeRef.current = false;
      return;
    }
    // when change selected vessel, at first location pathname is /dashboard/vesselId
    if (modeRef.current !== DASHBOARD_MODE.EDIT || !pathname.includes(latestDataRef.current.id)) {
      return;
    }
    // auto save current change
    await postDashboard(currentVessel.id, latestDataRef.current, true);
    // sync dashboards with new one
    setUpdateFlag(true);
  }, []);

  // to keep latest dashboard data and current mode
  useEffect(() => {
    latestDataRef.current = selectedDashboard;
    if (selectedDashboard)
      handleSelectedGraphChange(selectedDashboard.id);
  }, [selectedDashboard]);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  // when leave current location, this auto save function will be called
  useEffect(() => {
    return () => {
      handleFunction(location.pathname);
    };
  }, [location.pathname]);

  const handleSelectedGraphChange = async (nextDashboardId) => {
    if (isSaving) return;
    expectedChangeRef.current = true; // avoid to duplicate saving
    if (mode === DASHBOARD_MODE.EDIT) {
      // avoid unnecessary update fetching
      const previousDashboard = dashboards.find(
        (dashboard) => dashboard.id === selectedDashboard.id
      );
      const previousDashboardItems = previousDashboard?.items;
      if (JSON.stringify(previousDashboardItems) === JSON.stringify(selectedDashboard.items)) {
        setSelectedDashboard(dashboards.find((dashboard) => dashboard.id === nextDashboardId));
        navigate(`/dashboard/${nextDashboardId}/${currentVessel.id}`);
        return;
      }
      // kind of auto save behavior
      setIsSaving(true);
      await postDashboard(currentVessel.id, selectedDashboard);
      setIsSaving(false);
    } else if (mode === DASHBOARD_MODE.CREATE) {
      setMode(DASHBOARD_MODE.DISPLAY);
      setSelectedDashboard(dashboards.find((dashboard) => dashboard.id === nextDashboardId));
      navigate(`/dashboard/${nextDashboardId}/${currentVessel.id}`);
    } else {
      setSelectedDashboard(dashboards.find((dashboard) => dashboard.id === nextDashboardId));
      navigate(`/dashboard/${nextDashboardId}/${currentVessel.id}`);
    }
  };

  return (
    <Box component={"div"} sx={{ 
      ...styles.tabContainer,
      width: "calc(100vw - 77px)",
      [theme.breakpoints.down('sm')]: {
        width: sideBarStatus ? "calc(100vw - 77px)" : "calc(100vw)",
      },
    }}>
      <Box component={"div"} sx={{ ...styles.tabBase }}>
        <Stack sx={{ display: "flex" }} alignItems={"center"} spacing={2} direction={"row"}>
          {dashboards?.sort((a, b) => alphabeticalSortCompareFunction(a.name, b.name, "asc"))?.map((dashboard) => (
            <Button
              key={dashboard.id}
              onClick={async () => {
                handleSelectedGraphChange(dashboard.id);
              }}
              sx={{
                color: dashboard.id === selectedDashboard?.id ? COLORS.greenishBlue : "white",
                textTransform: "none",
              }}
            >
              {dashboard.name}
            </Button>
          ))}
          {mode === DASHBOARD_MODE.CREATE ? (
            <Button
              sx={{ color: COLORS.greenishBlue }}
            >{`New Custom Dashboard-${dashboards.length}`}</Button>
          ) : (
            <AddButton
              onClickHandler={() => {
                setMode(DASHBOARD_MODE.CREATE);
                setSelectedDashboard(null);
              }}
              disabled={false}
            />
          )}
        </Stack>
        <Box sx={{ marginLeft: "auto", color: "white" }}>
          <DashboardHeaderTools
            currentVessel={currentVessel}
            mode={mode}
            setMode={setMode}
            setIsDrawerOpen={setIsDrawerOpen}
            selectedDashboard={selectedDashboard}
            setSelectedDashboard={setSelectedDashboard}
            setDashboards={setDashboards}
            setAddComment={setAddComment}
          />
        </Box>
      </Box>
    </Box>
  );
};

DashboardHeader.propTypes = {
  currentVessel: PropTypes.object,
  mode: PropTypes.string,
  setMode: PropTypes.func,
  setIsDrawerOpen: PropTypes.func,
  dashboards: PropTypes.array,
  setDashboards: PropTypes.func,
  selectedDashboard: PropTypes.object,
  setSelectedDashboard: PropTypes.func,
  setAddComment: PropTypes.func,
};
